import type { BedOption, BedType } from '@room-mates/shared';
import type { ReactElement } from 'react';

export interface BedIllustrationProps {
  readonly type: BedType;
  readonly option: BedOption;
  readonly size?: 'sm' | 'lg';
}

const VIEW_WIDTH = 160;
const FLOOR_Y = 90;

/** How wide the bed sits in the scene — the one thing `type` alone controls. */
function frameFor(type: BedType): { x: number; width: number } {
  return type === 'double' ? { x: 14, width: 132 } : { x: 44, width: 72 };
}

/**
 * A side-view bed that visibly changes as its type and option change — the
 * point isn't photorealism, it's that picking "Bunk (top)" instead of
 * "Standard" obviously looks like a different piece of furniture.
 */
export function BedIllustration({ type, option, size = 'sm' }: BedIllustrationProps): ReactElement {
  const dimension = size === 'lg' ? 148 : 44;
  const { x, width } = frameFor(type);

  return (
    <span className={`bed-illustration bed-illustration--${size}`} aria-hidden="true">
      <svg
        key={`${type}-${option}`}
        className="pop-in"
        viewBox={`0 0 ${VIEW_WIDTH} 100`}
        width={dimension}
        height={(dimension * 100) / VIEW_WIDTH}
      >
        <line
          x1={6}
          y1={FLOOR_Y}
          x2={VIEW_WIDTH - 6}
          y2={FLOOR_Y}
          stroke="var(--border)"
          strokeWidth={2}
        />
        <ellipse
          cx={x + width / 2}
          cy={FLOOR_Y + 2}
          rx={width / 2 + 6}
          ry={4}
          fill="var(--border)"
          opacity={0.35}
        />
        <BedFurniture option={option} x={x} width={width} />
      </svg>
    </span>
  );
}

function BedFurniture({
  option,
  x,
  width,
}: {
  option: BedOption;
  x: number;
  width: number;
}): ReactElement {
  switch (option) {
    case 'bunk_top':
      return <Bunk x={x} width={width} occupiedLevel="top" />;
    case 'bunk_bottom':
      return <Bunk x={x} width={width} occupiedLevel="bottom" />;
    case 'sofa_bed':
      return <Sofa x={x} width={width} foldedOut />;
    case 'couch':
      return <Sofa x={x} width={width} foldedOut={false} />;
    case 'inflatable':
      return <Inflatable x={x} width={width} />;
    case 'standard':
    default:
      return <MadeBed x={x} width={width} />;
  }
}

/** Frame + mattress + pillow + blanket — every other option is a variation of this. */
function MadeBed({
  x,
  width,
  compact = false,
  dim = false,
}: {
  x: number;
  width: number;
  compact?: boolean;
  dim?: boolean;
}): ReactElement {
  const baseY = compact ? 62 : 74;
  const mattressY = compact ? 50 : 58;
  const mattressHeight = compact ? 12 : 18;
  const headTop = compact ? 40 : 30;
  const headBottom = baseY + 4;

  return (
    <g opacity={dim ? 0.4 : 1}>
      <rect
        x={x - 2}
        y={headTop}
        width={10}
        height={headBottom - headTop}
        rx={4}
        fill="var(--border-strong)"
      />
      <rect x={x} y={baseY} width={width} height={10} rx={3} fill="var(--border-strong)" />
      <rect
        x={x + 4}
        y={mattressY}
        width={width - 8}
        height={mattressHeight}
        rx={6}
        fill="var(--surface-raised)"
        stroke="var(--border-strong)"
        strokeWidth={2}
      />
      {dim ? null : (
        <>
          <rect
            x={x + 8}
            y={mattressY - 8}
            width={Math.min(34, width * 0.32)}
            height={16}
            rx={6}
            fill="var(--accent-soft)"
            stroke="var(--border-strong)"
            strokeWidth={1.5}
          />
          <rect
            x={x + width * 0.34}
            y={mattressY - 4}
            width={width * 0.62}
            height={mattressHeight + 4}
            rx={8}
            fill="var(--accent)"
          />
        </>
      )}
    </g>
  );
}

function Bunk({
  x,
  width,
  occupiedLevel,
}: {
  x: number;
  width: number;
  occupiedLevel: 'top' | 'bottom';
}): ReactElement {
  return (
    <g>
      <rect x={x - 4} y={14} width={8} height={76} rx={3} fill="var(--border-strong)" />
      <rect x={x + width - 4} y={14} width={8} height={76} rx={3} fill="var(--border-strong)" />
      <line
        x1={x + width - 4}
        y1={44}
        x2={x + width + 4}
        y2={44}
        stroke="var(--border-strong)"
        strokeWidth={3}
      />
      <line
        x1={x + width - 4}
        y1={54}
        x2={x + width + 4}
        y2={54}
        stroke="var(--border-strong)"
        strokeWidth={3}
      />
      <line
        x1={x + width - 4}
        y1={64}
        x2={x + width + 4}
        y2={64}
        stroke="var(--border-strong)"
        strokeWidth={3}
      />
      <g transform="translate(0, -22)">
        <MadeBed x={x} width={width} compact dim={occupiedLevel !== 'top'} />
      </g>
      <MadeBed x={x} width={width} compact dim={occupiedLevel !== 'bottom'} />
    </g>
  );
}

/**
 * A couch's silhouette from the side: a tall back at one end, a low armrest
 * at the other, a cushion between — nothing like a bed's flat mattress, which
 * is the point. `foldedOut` swaps the seat cushion for a mattress sliding out
 * past the armrest, the way a real sofa bed actually opens.
 */
function Sofa({
  x,
  width,
  foldedOut,
}: {
  x: number;
  width: number;
  foldedOut: boolean;
}): ReactElement {
  const backWidth = Math.min(22, width * 0.24);
  const armWidth = Math.min(18, width * 0.2);
  const seatTop = 48;
  const seatBottom = 74;

  return (
    <g>
      {/* legs */}
      <rect x={x + 6} y={80} width={6} height={8} rx={2} fill="var(--border-strong)" />
      <rect x={x + width - 12} y={80} width={6} height={8} rx={2} fill="var(--border-strong)" />

      {/* tall back, low arm — the two-height profile a bed never has */}
      <rect
        x={x}
        y={22}
        width={backWidth}
        height={seatBottom - 22}
        rx={10}
        fill="var(--border-strong)"
      />
      <rect
        x={x + width - armWidth}
        y={40}
        width={armWidth}
        height={seatBottom - 40}
        rx={9}
        fill="var(--border-strong)"
      />

      {foldedOut ? (
        <>
          {/* the couch, still visibly a couch, folded flat under the mattress */}
          <rect
            x={x + backWidth * 0.6}
            y={seatTop + 10}
            width={width - backWidth * 0.6 - armWidth * 0.6}
            height={seatBottom - seatTop - 6}
            rx={8}
            fill="var(--surface-sunken)"
            opacity={0.6}
          />
          <rect
            x={x + backWidth * 0.5}
            y={seatBottom - 4}
            width={width - backWidth * 0.5}
            height={12}
            rx={5}
            fill="var(--accent-soft)"
            stroke="var(--border-strong)"
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
          <rect
            x={x + backWidth * 0.8}
            y={seatBottom - 12}
            width={Math.min(28, width * 0.26)}
            height={11}
            rx={4}
            fill="var(--accent)"
          />
        </>
      ) : (
        <>
          <rect
            x={x + backWidth * 0.6}
            y={seatTop}
            width={width - backWidth * 0.6 - armWidth * 0.6}
            height={seatBottom - seatTop}
            rx={9}
            fill="var(--surface-raised)"
            stroke="var(--border-strong)"
            strokeWidth={2}
          />
          <line
            x1={x + width / 2}
            y1={seatTop + 4}
            x2={x + width / 2}
            y2={seatBottom - 4}
            stroke="var(--border)"
            strokeWidth={1.5}
          />
        </>
      )}
    </g>
  );
}

function Inflatable({ x, width }: { x: number; width: number }): ReactElement {
  const bump = (fraction: number, up: number): string =>
    `Q ${x + width * fraction} ${up} ${x + width * (fraction + 0.15)} 72`;
  const d = [
    `M ${x} 78`,
    bump(0.15, 60),
    bump(0.45, 58),
    bump(0.75, 60),
    `Q ${x + width} 78 ${x + width} 82`,
    `L ${x} 82`,
    'Z',
  ].join(' ');

  return (
    <g>
      <path
        d={d}
        fill="var(--accent-soft)"
        stroke="var(--border-strong)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      <circle cx={x + width - 14} cy={78} r={4} fill="var(--border-strong)" />
    </g>
  );
}
